
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UseOptimizedPersonalPlanDetectorProps {
  routineId: string;
  enabled?: boolean;
}

export const useOptimizedPersonalPlanDetector = ({
  routineId,
  enabled = true
}: UseOptimizedPersonalPlanDetectorProps) => {
  const query = useQuery({
    queryKey: ["optimized-personal-plan-detector", routineId],
    queryFn: async () => {
      if (!routineId) return false;

      console.log(`🔍 DEBUG: Personal Plan Detection for routine ${routineId}`);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log(`❌ DEBUG: No user found for personal plan detection`);
        return false;
      }

      console.log(`👤 DEBUG: User found: ${user.id}`);

      // Query ultra-optimizada - solo verificar si la rutina está en el plan personal del usuario
      const { data, error } = await supabase
        .from("workout_plan_routines")
        .select(`
          workout_plans!inner(is_personal, created_by, name)
        `)
        .eq("routine_id", routineId)
        .eq("workout_plans.created_by", user.id)
        .eq("workout_plans.is_personal", true)
        .limit(1);

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error("❌ DEBUG: Error in optimized personal plan detection:", error);
        return false;
      }

      const isPersonal = !!data && data.length > 0;
      console.log(`✅ DEBUG: Personal plan detection result for ${routineId}:`, {
        isPersonal,
        dataFound: data?.length || 0,
        planDetails: data?.[0]?.workout_plans
      });
      
      return isPersonal;
    },
    enabled: enabled && !!routineId,
    staleTime: 300000, // 5 minutos
    gcTime: 600000, // 10 minutos
    retry: 1, // Solo un retry para mantener velocidad
  });

  return {
    isPersonalPlan: query.data ?? false,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch
  };
};
